// Sales activities (PHASED-PLAN P5 S1/S2): one record per call, email, meeting, note, task, SMS or WhatsApp message with
// a lead, contact, deal or client. The collection is `activities`; it is separate from `activity`, the workspace-wide
// "who did what" feed in activity.ts. Every module logs through `logActivity` (packages/mod-sales), and the timeline of
// a record is a read over these rows. What a call came to ("Connected", "Voicemail") is agency data: `activityOutcomes`.
import { z } from "zod";
import { BASE, HUE, ISO, ISO_DATE, serverOwned } from "./common.ts";

/** What happened. A fixed set the product understands (each kind has its own icon and fields); the outcome of it is data. */
export const ACTIVITY_KINDS = ["call", "email", "meeting", "note", "task", "sms", "whatsapp"] as const;
export const ACTIVITY_KIND = z.enum(ACTIVITY_KINDS);
export type ActivityKind = z.infer<typeof ACTIVITY_KIND>;

/** The record an activity belongs to. A lead is a person in an outreach list (`leads`). */
export const ACTIVITY_SUBJECTS = ["lead", "contact", "deal", "client"] as const;
export const ACTIVITY_SUBJECT = z.enum(ACTIVITY_SUBJECTS);
export type ActivitySubject = z.infer<typeof ACTIVITY_SUBJECT>;

/** Where a call is: a manual log is `done`; a phone call moves through ringing and in progress to done, missed or failed. */
export const ACTIVITY_STATUS = z.enum(["done", "ringing", "in_progress", "missed", "failed"]);
export type ActivityStatus = z.infer<typeof ACTIVITY_STATUS>;

/**
 * One activity. `subjectType`/`subjectId` name the record it is on (both empty only for an inbound call from a number no
 * record has yet). The server fills `clientId` so a client's timeline also shows what happened on its deals and contacts,
 * and keeps the deal's `lastActivity` current (packages/db/sql/R-58-activities.sql).
 */
export const SALES_ACTIVITY = serverOwned(BASE.extend({
  kind: ACTIVITY_KIND,
  subjectType: ACTIVITY_SUBJECT.optional(),
  subjectId: z.string().optional(),
  /** The client the activity rolls up to (the deal's client, the contact's first client, or the client itself). */
  clientId: z.string().optional(),
  /** The rep who did it (or took the call). */
  ownerId: z.string().optional(),
  direction: z.enum(["inbound", "outbound"]).optional(),
  /** A subject line the person typed ("Pricing questions"); empty for a plain call, where the screen words it. */
  title: z.string().default(""),
  notes: z.string().optional(),
  /** The outcome picked from `activityOutcomes`; `outcome` keeps its label as it was, so an old call still reads right after a rename. */
  outcomeId: z.string().optional(),
  outcome: z.string().optional(),
  occurredAt: ISO,
  durationSec: z.number().int().min(0).optional(),
  nextStep: z.string().optional(),
  nextStepAt: ISO.optional(),
  /** When the next step was marked done; empty while it is open. Written by the wrap-up action only. */
  nextStepDoneAt: ISO.optional(),
  /** The number called or calling, in E.164. */
  phone: z.string().optional(),
  /** The person who placed the call from the business number (X15). Set by the server from the caller; empty for an inbound call. */
  placedBy: z.string().optional(),
  /** The workspace's shared business number the call was placed from, in E.164 (X15). */
  fromNumber: z.string().max(32).optional(),
  status: ACTIVITY_STATUS.default("done"),
  /** `manual` for a person's log, otherwise the connector that wrote it (`twilio`). */
  source: z.string().default("manual"),
  /** The provider's id for the call (a Twilio CallSid): a replayed callback updates the same row. */
  externalId: z.string().optional(),
  recordingSid: z.string().optional(),
  recordingSec: z.number().int().min(0).optional(),
  /** The provider's page for the recording (Aircall's asset page), for a person to open; not the signed audio link. */
  recordingUrl: z.string().max(2000).optional(),
  /** The recording and the transcript as files in the workspace once they are stored. */
  recordingFileId: z.string().optional(),
  transcriptFileId: z.string().optional(),
  /** The words of a recording, written by the transcription job once the speech provider answers (X13-1310a). */
  transcript: z.string().optional(),
  /** The timed lines of the transcript (start and end in seconds from the recording's start), when the speech provider gives them (X21-CRM-11). */
  transcriptSegments: z.array(z.object({ start: z.number().min(0), end: z.number().min(0), text: z.string().max(2000) })).max(20000).optional(),
  /** When the reply target was missed for this inbound message. The sales sweep sets it once. */
  responseBreachedAt: ISO.optional(),
  /** Practice data for the walkthroughs (T1-05a): set by the server only. */
  practice: z.boolean().optional(),
}), ["clientId", "status", "source", "externalId", "recordingSid", "recordingSec", "recordingFileId", "transcriptFileId", "transcript", "transcriptSegments", "nextStepDoneAt", "responseBreachedAt", "placedBy", "fromNumber", "practice"]);
export type SalesActivity = z.infer<typeof SALES_ACTIVITY>;

/** How a call (or any activity) ended, as the agency names it. `reached` marks the outcomes that count as a conversation in rep stats. */
export const ACTIVITY_OUTCOME = BASE.extend({
  label: z.string().trim().min(1).max(40),
  hue: HUE,
  reached: z.boolean(),
  position: z.number().int().min(0),
  archived: z.boolean().default(false),
});
export type ActivityOutcome = z.infer<typeof ACTIVITY_OUTCOME>;

/** The standard set a new workspace can start from (Calls page, "Add the standard outcomes"); the agency edits it freely. */
export const STANDARD_OUTCOMES: { label: string; hue: z.infer<typeof HUE>; reached: boolean }[] = [
  { label: "Connected", hue: "fern", reached: true },
  { label: "Meeting booked", hue: "jade", reached: true },
  { label: "Follow up later", hue: "saffron", reached: true },
  { label: "Not interested", hue: "rose", reached: true },
  { label: "Left voicemail", hue: "sky", reached: false },
  { label: "No answer", hue: "apricot", reached: false },
  { label: "Wrong number", hue: "ember", reached: false },
];

/** How a rule picks among its reps: the one given a lead longest ago (round robin) or the one with the fewest open leads. */
export const ASSIGN_STRATEGY = z.enum(["round_robin", "least_loaded"]);
export type AssignStrategy = z.infer<typeof ASSIGN_STRATEGY>;

/** A named region (territory): a group of time-zone prefixes that rules can match by region id. */
export const ASSIGN_REGION = z.strictObject({
  id: z.string().min(1).max(60),
  name: z.string().trim().min(1).max(60),
  timeZonePrefixes: z.array(z.string().trim().min(1).max(40)).min(1).max(20),
});
export type AssignRegion = z.infer<typeof ASSIGN_REGION>;

/** Which inbound leads a rule takes: lead sources, lead lists (segments), a time-zone prefix (territory) and regions. Empty means any. */
export const ASSIGN_MATCH = z.strictObject({
  sources: z.array(z.string().trim().min(1).max(60)).max(30).default([]),
  listIds: z.array(z.string().min(1).max(80)).max(30).default([]),
  timeZonePrefix: z.string().trim().max(40).default(""),
  /** Region ids (SALES_SETTING.regions); a lead whose time zone starts with any prefix of any listed region is taken. A deleted region matches nothing. */
  regionIds: z.array(z.string().min(1).max(60)).max(30).optional(),
});

/** One assignment rule. Rules run in order; the first enabled rule that matches the lead decides who gets it. */
export const ASSIGN_RULE = z.strictObject({
  id: z.string().min(1).max(60),
  name: z.string().trim().min(1).max(80),
  enabled: z.boolean().default(true),
  match: ASSIGN_MATCH.default({ sources: [], listIds: [], timeZonePrefix: "" }),
  strategy: ASSIGN_STRATEGY.default("round_robin"),
  /** The people the rule can give leads to (person ids, staff). */
  reps: z.array(z.string().min(1).max(80)).min(1).max(50),
  /** A rep holding this many open leads takes no more. 0 means no cap. */
  maxOpen: z.number().int().min(0).max(10_000).default(0),
  /** Reps inside their own working hours get the lead first; when none is on shift, any rep does. */
  respectWorkingHours: z.boolean().default(true),
});
export type AssignRule = z.infer<typeof ASSIGN_RULE>;

/**
 * A commission plan (X16-A06a). It applies to one person, team or role, and the plan kind that matches most closely wins
 * (person, then team, then role). `basis` says what earns commission: a deal's value when it is won, or money collected
 * on its invoices. Tiers are read against the period quota: the rate of the tier the attainment is in earns each dollar.
 * A tier above 100% is an accelerator. `capAmount` limits a period's total; `clawback` takes commission back when a
 * credit note reverses a deal credited in an earlier period.
 */
export const COMMISSION_PLAN = z
  .strictObject({
    id: z.string().min(1).max(40),
    name: z.string().trim().min(1).max(120),
    appliesTo: z.strictObject({ kind: z.enum(["person", "team", "role"]), id: z.string().min(1).max(80) }),
    basis: z.enum(["deal_value", "collected"]),
    serviceIds: z.array(z.string().max(80)).max(50).default([]),
    tiers: z.array(z.strictObject({ fromPctOfQuota: z.number().min(0).max(1000), ratePct: z.number().min(0).max(100) })).min(1).max(10),
    capAmount: z.number().min(0).max(1_000_000_000_000).nullable().default(null),
    clawback: z.boolean().default(true),
    effectiveFrom: ISO_DATE,
    effectiveTo: ISO_DATE.nullable().default(null),
  })
  .superRefine((p, ctx) => {
    if (p.tiers[0]?.fromPctOfQuota !== 0) ctx.addIssue({ code: "custom", path: ["tiers"], message: "The first tier must start at 0%." });
    p.tiers.forEach((t, i) => {
      if (i > 0 && t.fromPctOfQuota <= (p.tiers[i - 1]?.fromPctOfQuota ?? 0)) ctx.addIssue({ code: "custom", path: ["tiers", i], message: "Tiers must rise." });
    });
    if (p.effectiveTo !== null && p.effectiveTo < p.effectiveFrom) ctx.addIssue({ code: "custom", path: ["effectiveTo"], message: "The plan ends before it starts." });
  });
export type CommissionPlan = z.infer<typeof COMMISSION_PLAN>;

/**
 * The workspace's sales settings (Settings key `sales`). `speedToLeadMinutes` is the first-touch target for an inbound
 * lead, in business minutes; `reassignAfterMinutes` moves a lead to the next rep when its owner has not touched it for
 * that long (0 keeps leads where they are). Quotas and goals are per person: a quota per month or quarter, and daily
 * call and weekly meeting goals.
 */
export const SALES_SETTING = z.strictObject({
  rules: z.array(ASSIGN_RULE).max(30).default([]),
  regions: z.array(ASSIGN_REGION).max(50).default([]),
  speedToLeadMinutes: z.number().int().min(0).max(10_080).default(60),
  reassignAfterMinutes: z.number().int().min(0).max(10_080).default(0),
  /** Reply target per channel in business minutes; 0 turns a channel's target off. An inbound message with no outbound reply on the same record and channel inside it is a breach. */
  responseTargets: z
    .strictObject({
      email: z.number().int().min(0).max(10_080),
      sms: z.number().int().min(0).max(10_080),
      whatsapp: z.number().int().min(0).max(10_080),
      call: z.number().int().min(0).max(10_080),
    })
    .default({ email: 0, sms: 0, whatsapp: 0, call: 0 }),
  quotas: z
    .array(z.strictObject({ personId: z.string().min(1).max(80), period: z.enum(["month", "quarter"]), amount: z.number().min(0).max(1_000_000_000_000) }))
    .max(200)
    .default([]),
  goals: z
    .array(z.strictObject({ personId: z.string().min(1).max(80), callsPerDay: z.number().int().min(0).max(500).default(0), meetingsPerWeek: z.number().int().min(0).max(200).default(0) }))
    .max(200)
    .default([]),
  /** Commission plans (X16-A06a): agency-editable rules that feed the per-period statement of each rep. */
  commissions: z.array(COMMISSION_PLAN).max(100).default([]),
});
export type SalesSetting = z.infer<typeof SALES_SETTING>;
export const DEFAULT_SALES_SETTING: SalesSetting = SALES_SETTING.parse({});
